import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateDataGridSx,
  harmonateCardSx,
  harmonateAccentLinkSx,
} from '../../../theme/harmonateStyles';

/** `parseFloat("12/31/2024")` is 12 — only treat values as numbers when the whole cell is a plain decimal. */
const isGridNumericCellValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  const t = value.trim();
  if (t === '') return false;
  return /^-?\d+(\.\d+)?$/.test(t);
};

const parseGridNumericCellValue = (value) =>
  typeof value === 'number' ? value : parseFloat(String(value).trim());

/** Keep API formatting (e.g. `612000.00`); avoid `parseFloat` + `toString()` dropping `.00`. */
const formatGridNumericDisplayText = (value, numericValue) => {
  if (typeof value === 'string' && isGridNumericCellValue(value)) return value.trim();
  return Number.isFinite(numericValue) ? numericValue.toString() : '';
};

const ViewDataDialog = ({ 
  open, 
  onClose, 
  data, 
  loading, 
  error, 
  fileName 
}) => {
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState(null);
  const [selectedJsonField, setSelectedJsonField] = useState('');

  // Format header names to be more readable
  const formatHeaderName = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleViewJson = (jsonData, fieldName) => {
    setSelectedJsonData(jsonData);
    setSelectedJsonField(fieldName);
    setJsonViewerOpen(true);
  };

  const handleCloseJsonViewer = () => {
    setJsonViewerOpen(false);
    setSelectedJsonData(null);
    setSelectedJsonField('');
  };

  // Transform data for DataGrid - use JSON field names as column headers
  const transformedData = React.useMemo(() => {
    if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
      return { columns: [], rows: [] };
    }

    // Extract all JSON objects and flatten them into records
    const records = [];
    const allFieldNames = new Set();

    data.data.forEach((item, itemIndex) => {
      Object.keys(item).forEach(key => {
        const value = item[key];
        
        // If it's a JSON object, treat it as a record
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          const record = { id: `${itemIndex}-${key}`, ...value };
          records.push(record);
          
          // Add all field names from this JSON object
          Object.keys(value).forEach(fieldName => {
            allFieldNames.add(fieldName);
          });
        } else if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
          try {
            const parsedValue = JSON.parse(value);
            const record = { id: `${itemIndex}-${key}`, ...parsedValue };
            records.push(record);
            
            // Add all field names from this parsed JSON
            Object.keys(parsedValue).forEach(fieldName => {
              allFieldNames.add(fieldName);
            });
          } catch (error) {
            // If parsing fails, skip this field
          }
        }
      });
    });

    const numericFieldNames = new Set();
    records.forEach((record) => {
      allFieldNames.forEach((fieldName) => {
        if (isGridNumericCellValue(record[fieldName])) {
          numericFieldNames.add(fieldName);
        }
      });
    });

    // Create columns from all unique field names
    const columns = Array.from(allFieldNames).map((fieldName) => ({
      field: fieldName,
      headerName: formatHeaderName(fieldName),
      width: 200,
      flex: 1,
      minWidth: 150,
      align: numericFieldNames.has(fieldName) ? 'right' : 'left',
      headerAlign: numericFieldNames.has(fieldName) ? 'right' : 'left',
      sortable: true,
      filterable: true,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === '') {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        
        // Handle nested JSON objects - show as View button
        if (typeof value === 'object' && value !== null) {
          return (
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => handleViewJson(value, fieldName)}
              sx={{ 
                ...harmonateAccentLinkSx,
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.75rem'
              }}
            >
              View
            </Button>
          );
        }
        
        // Plain decimals first — avoids misclassifying `-51000.00` as a "date" via `includes('-')`.
        if (isGridNumericCellValue(value)) {
          const numericValue = parseGridNumericCellValue(value);
          const displayText = formatGridNumericDisplayText(value, numericValue);
          return (
            <Box sx={{ width: '100%', textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  color: numericValue < 0 ? ht.red : numericValue > 0 ? ht.green : ht.text,
                  fontWeight: numericValue !== 0 ? 500 : 400,
                }}
              >
                {displayText}
              </Typography>
            </Box>
          );
        }

        // ISO-like date/timestamp strings (not slash dates like 12/31/2024)
        if (
          typeof value === 'string' &&
          (value.includes('T') || (value.includes('-') && value.includes(':')) || /^\d{4}-\d{2}-\d{2}/.test(value.trim()))
        ) {
          return (
            <Typography variant="body2" sx={{ maxWidth: '100%' }}>
              {String(value)}
            </Typography>
          );
        }
        
        // For other values, show as text
        return (
          <Typography variant="body2" sx={{ maxWidth: '100%' }}>
            {String(value)}
          </Typography>
        );
      }
    }));

    return { columns, rows: records };
  }, [data]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          ...harmonateDialogPaperProps.sx,
          height: '90vh',
          maxHeight: '90vh',
          width: '95vw',
          maxWidth: '95vw',
        }
      }}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>
        <Box>
          <Typography variant="h6" component="div" sx={{ color: ht.white, fontWeight: 700 }}>
            View Data
          </Typography>
          {fileName && (
            <Typography variant="body2" sx={{ color: ht.white }}>
              {fileName}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={harmonateDialogCloseIconSx}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ ...harmonateDialogContentSx, p: 2, overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px' 
          }}>
            <CircularProgress sx={{ color: ht.blue }} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading JSON records...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && transformedData.rows.length > 0 && (
          <Box sx={{ height: '100%', width: '100%' }}>
            <DataGridPro
             columnResizeMode="onEnd"
              pagination
             // rowsPerPageOptions={[10, 25, 50]}
             // pageSize={10}
              autoPageSize={false}
             resizable
              rows={transformedData.rows}
              columns={transformedData.columns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              sx={harmonateDataGridSx}
            />
          </Box>
        )}

        {!loading && !error && transformedData.rows.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px' 
          }}>
            <Typography variant="body1" color="text.secondary">
              No JSON records available to display
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* JSON Viewer Dialog */}
      <Dialog
        open={jsonViewerOpen}
        onClose={handleCloseJsonViewer}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...harmonateDialogPaperProps.sx,
            height: '80vh',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Typography variant="h6" component="div" sx={{ color: ht.white, fontWeight: 700 }}>
            JSON Viewer - {selectedJsonField}
          </Typography>
          <IconButton
            onClick={handleCloseJsonViewer}
            size="small"
            sx={harmonateDialogCloseIconSx}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ 
            p: 2, 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: ht.listBg
          }}>
            <Paper sx={{ ...harmonateCardSx, p: 2, bgcolor: ht.white }}>
              <pre style={{ 
                margin: 0, 
                fontSize: '12px', 
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {JSON.stringify(selectedJsonData, null, 2)}
              </pre>
            </Paper>
          </Box>
        </DialogContent>

      </Dialog>
    </Dialog>
  );
};

export default ViewDataDialog;
